import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { makeStationDetailsUrl } from '../../../routes';
import { useGetHousesQuery } from '../../../api/inventory/inventoryApiSlice';
import type { AssetDetailsResponse } from '../../../api/asset/assetApiTypes';
import AssignToHouseSection from './AssignToHouseSection';
import { assignableHouses } from '../houseConsolidation';
import { isRoomCell } from '../../../utils/roomUseCases';

interface Props {
    data: AssetDetailsResponse;
    mode: 'view' | 'edit';
    /** The room's hours are the house's and cannot be edited here (A1 a). */
    hoursLocked: boolean;
    /** The edit holds changes a save would write; assigning waits for them. */
    hasUnsavedChanges: boolean;
    /** Called once the room is in the house: the edit ends. */
    onAssigned: () => void;
}

/**
 * A room and its house, on the room page (plan
 * `room-houses-and-combining-rooms.md` §4 rows 2–3):
 *
 *  - the house it stands in, linked;
 *  - in edit mode, why its opening hours are read-only;
 *  - "Dem Haus zuordnen", when the room stands alone in its house (or on an
 *    automatic one-room station from before 2026-10-12) while another house
 *    stands at its identical address (ADR-0025 amendment 2026-10-12) — in
 *    edit mode only, like every other change to the room (PO, 2026-10-08):
 *    outside "Bearbeiten" nothing here changes it.
 */
const RoomHouseSection = ({ data, mode, hoursLocked, hasUnsavedChanges, onAssigned }: Props) => {
    const { t } = useTranslation();
    const isRoom = isRoomCell(data.Group?.compartmentTypes);
    const { data: houses = [], isError: housesError } = useGetHousesQuery(
        undefined,
        { skip: !isRoom },
    );
    const targets = assignableHouses(houses, data.house?.id ?? null, data.Group?.location?.address);

    return (
        <>
            {data.house && (
                <p className={'text-sm text-foreground/60'}>
                    {t('asset_detail.house.in_house')}{' '}
                    <Link to={makeStationDetailsUrl(data.house.id)} className={'text-primary hover:underline'}>
                        {data.house.name}
                    </Link>
                </p>
            )}
            {mode === 'edit' && hoursLocked && (
                <p className={'text-sm text-foreground/60'}>{t('asset_detail.house.hours_shared')}</p>
            )}
            {mode === 'edit' && targets.length > 0 && (
                <AssignToHouseSection
                    assetId={data.id}
                    houses={targets}
                    ownLocationId={data.Group?.location?.id ?? null}
                    blockedByUnsavedChanges={hasUnsavedChanges}
                    onAssigned={onAssigned}
                />
            )}
            {/* A failed house list is said, not shown as "no house here". */}
            {mode === 'edit' && housesError && (
                <p className={'text-sm text-foreground/60'}>{t('asset_detail.house.houses_error')}</p>
            )}
        </>
    );
};

export default RoomHouseSection;
